// INTELIMOVE-XPR-005 (IM-UX-119) — Mapeamento oferta × ICP do objetivo "Rever minhas ofertas". Puro: sem banco, sem
// rede, sem IA. Mesma regra no servidor (leitor da etapa J4-02, progresso) e na tela (sugestões, conclusão).
//
// Regra de conclusão (documentada em docs/requirements/EXPERIENCIA_OBJETIVOS.md §XPR-005): uma oferta confirmada está
// TRATADA quando (a) está relacionada a pelo menos um ICP confirmado, OU (b) foi marcada como transversal/institucional,
// OU (c) foi marcada como "sem público definido" — esta última conta como tratada, mas fica listada para tratamento
// posterior. O mapeamento está CONCLUÍDO quando todas as ofertas confirmadas estão tratadas.
//
// Sugestão: aproximação por termos entre o que a oferta diz (nome, descrição, problemas, benefícios, público, segmentos)
// e o que o ICP diz (nome, descrição, segmentos, dores, necessidades, gatilhos, tecnologias), com um vocabulário pequeno de
// conceitos equivalentes. Nunca grava nada: a sugestão só vira relação quando a pessoa confirma. Toda sugestão diz o
// motivo (termos em comum), a confiança e os dados usados. Sem termos suficientes, não há sugestão (nada é inventado).

import { z } from 'zod';

export type AudienceScope = 'TRANSVERSAL' | 'SEM_PUBLICO';
export const AUDIENCE_SCOPE_LABEL: Record<AudienceScope, string> = { TRANSVERSAL: 'Transversal ou institucional', SEM_PUBLICO: 'Sem público definido' };

export interface MapOffering {
  id: string; name: string; description?: string | null; problemsSolved?: string[]; benefits?: string[]; targetAudience?: string | null;
  segments?: string[]; icpIds: string[]; audienceScope?: AudienceScope | null;
}
export interface MapIcp {
  id: string; name: string; description?: string | null; segments?: string[]; pains?: string[]; needs?: string[]; triggers?: string[]; technologies?: string[];
}

export type OfferingTreatment = 'RELACIONADA' | 'TRANSVERSAL' | 'SEM_PUBLICO' | 'PENDENTE';
export function offeringTreatment(o: Pick<MapOffering, 'icpIds' | 'audienceScope'>): OfferingTreatment {
  if (o.icpIds.length) return 'RELACIONADA';
  if (o.audienceScope === 'TRANSVERSAL') return 'TRANSVERSAL';
  if (o.audienceScope === 'SEM_PUBLICO') return 'SEM_PUBLICO';
  return 'PENDENTE';
}
export const OFFERING_TREATMENT_LABEL: Record<OfferingTreatment, string> = {
  RELACIONADA: 'Relacionada a perfis de cliente', TRANSVERSAL: 'Transversal ou institucional', SEM_PUBLICO: 'Sem público definido (tratar depois)', PENDENTE: 'Aguardando revisão',
};

export interface OfferingMapProgress { total: number; treated: number; related: number; transversal: number; noAudience: number; pending: number; done: boolean }
export function offeringMapProgress(list: ReadonlyArray<Pick<MapOffering, 'icpIds' | 'audienceScope'>>): OfferingMapProgress {
  const t = list.map(offeringTreatment);
  const n = (k: OfferingTreatment) => t.filter((x) => x === k).length;
  const pending = n('PENDENTE');
  return { total: list.length, treated: list.length - pending, related: n('RELACIONADA'), transversal: n('TRANSVERSAL'), noAudience: n('SEM_PUBLICO'), pending, done: list.length > 0 && pending === 0 };
}

// ── Sugestão por termos ─────────────────────────────────────────────────────────────────────────────────────────────
const STOP = new Set(('a o os as de do da dos das e em no na nos nas para por com sem que um uma uns umas ao aos à às seu sua seus suas ' +
  'mais menos como ou se ser ter tem sua cada todo toda todos todas muito pouco qual quais onde quando entre sobre até após também já ' +
  'empresa empresas cliente clientes servico servicos produto produtos solucao solucoes negocio negocios time times area areas').split(' '));
/** Normaliza para comparação: minúsculas, sem acento, sem plural simples. */
export function stemPt(w: string): string {
  let s = w.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  if (s.length > 4) s = s.replace(/(oes|aes)$/, 'ao').replace(/(ais)$/, 'al').replace(/(eis)$/, 'el').replace(/(ns)$/, 'm').replace(/([^s])s$/, '$1');
  return s;
}
export function termsOf(...parts: Array<string | null | undefined | string[]>): Set<string> {
  const out = new Set<string>();
  for (const p of parts.flat()) for (const w of String(p ?? '').split(/[^\p{L}\p{N}]+/u)) {
    const s = stemPt(w);
    if (s.length >= 3 && !STOP.has(s)) out.add(s);
  }
  return out;
}
/**
 * Conceitos equivalentes (genéricos de negócio e tecnologia, não de um cliente): cada grupo liga palavras que costumam
 * descrever o mesmo problema dos dois lados ("performance" na oferta ↔ "picos de acesso" no ICP).
 */
const CONCEPTS: ReadonlyArray<readonly string[]> = [
  ['performance', 'desempenho', 'carga', 'estresse', 'pico', 'volume', 'lentidao', 'escala', 'escalabilidade', 'acesso', 'trafego'],
  ['seguranca', 'pentest', 'vulnerabilidade', 'sensivel', 'regulado', 'regulacao', 'conformidade', 'lgpd', 'compliance', 'privacidade', 'fraude'],
  ['regressao', 'legado', 'erp', 'quebra', 'mudanca', 'sap', 'corporativo', 'integracao', 'manutencao'],
  ['automacao', 'automatizado', 'release', 'entrega', 'ciclo', 'continuo', 'pipeline', 'devops', 'agil', 'frequente', 'sprint'],
  ['api', 'apis', 'integracao', 'servico', 'microsservico', 'backend'],
  ['ecommerce', 'e-commerce', 'varejo', 'loja', 'checkout', 'venda', 'marketplace'],
  ['saas', 'software', 'plataforma', 'digital', 'aplicativo', 'app', 'produto'],
  ['financeiro', 'banco', 'fintech', 'pagamento', 'credito', 'seguradora'],
  ['saude', 'hospital', 'clinica', 'paciente'],
  ['consultoria', 'diagnostico', 'maturidade', 'avaliacao', 'assessment', 'estrategia'],
  ['outsourcing', 'alocacao', 'terceirizacao', 'dedicado', 'squad', 'celula'],
  ['ia', 'inteligencia', 'artificial', 'ml', 'llm'],
].map((g) => g.map(stemPt));
const conceptOf = (t: string): number[] => CONCEPTS.flatMap((g, i) => (g.includes(t) ? [i] : []));

export interface IcpSuggestion {
  icpId: string; icpName: string; score: number; confidence: 'ALTA' | 'MEDIA' | 'BAIXA';
  /** Termos e conceitos em comum, em linguagem de leitura. */
  matched: string[];
  reason: string;
}
export interface OfferingSuggestion {
  icps: IcpSuggestion[];
  /** Segmentos dos ICPs sugeridos (para relacionar junto). */
  segments: string[];
  /** Combina com quase todos os perfis por igual: provável oferta transversal. */
  transversal: boolean;
  /** Dados usados para sugerir (transparência). */
  basedOn: string[];
  /** Sem sugestão: por quê. */
  none: string | null;
}

/** Sugere ICPs para UMA oferta. Determinístico; empate resolvido pelo nome do ICP. */
export function suggestIcpsForOffering(o: MapOffering, icps: readonly MapIcp[]): OfferingSuggestion {
  const ot = termsOf(o.name, o.description, o.problemsSolved, o.benefits, o.targetAudience, o.segments);
  const basedOn = [
    'nome da oferta', o.description ? 'descrição' : null, o.problemsSolved?.length ? 'problemas resolvidos' : null, o.benefits?.length ? 'benefícios' : null,
    o.targetAudience ? 'público declarado' : null, o.segments?.length ? 'segmentos da oferta' : null, 'descrição, segmentos, dores e necessidades de cada ICP',
  ].filter((x): x is string => !!x);
  if (!icps.length) return { icps: [], segments: [], transversal: false, basedOn, none: 'Não há perfil de cliente (ICP) confirmado para relacionar.' };
  const oConcepts = new Set([...ot].flatMap(conceptOf));
  const scored = icps.map((i) => {
    const it = termsOf(i.name, i.description, i.segments, i.pains, i.needs, i.triggers, i.technologies);
    const direct = [...ot].filter((t) => it.has(t));
    const iConcepts = new Set([...it].flatMap(conceptOf));
    const shared = [...oConcepts].filter((c) => iConcepts.has(c));
    const score = Math.min(1, direct.length * 0.25 + shared.length * 0.3);
    const words = [...new Set([...direct, ...shared.map((c) => CONCEPTS[c]![0]!)])];
    return { icpId: i.id, icpName: i.name, score: Math.round(score * 100) / 100, matched: words.slice(0, 5), segments: i.segments ?? [] };
  }).sort((a, b) => b.score - a.score || a.icpName.localeCompare(b.icpName, 'pt-BR'));
  const good = scored.filter((s) => s.score >= 0.3);
  // Transversal: combina com 3+ perfis com força parecida (ou com todos, havendo 3+), sem um perfil claramente à frente.
  const transversal = icps.length >= 3 && good.length >= Math.max(3, icps.length - 1) && good[0]!.score - good[good.length - 1]!.score <= 0.25;
  if (!good.length) {
    return { icps: [], segments: [], transversal: false, basedOn,
      none: ot.size <= 3 ? 'A oferta só tem nome; sem descrição não dá para relacionar com segurança. Escolha os perfis ou marque como transversal.' : 'Nenhum perfil tem termos em comum suficientes com esta oferta.' };
  }
  const top = (transversal ? good : good.filter((s) => s.score >= Math.max(0.3, good[0]!.score - 0.3))).slice(0, 3);
  const list = top.map((s): IcpSuggestion => {
    const confidence = s.score >= 0.7 ? 'ALTA' : s.score >= 0.45 ? 'MEDIA' : 'BAIXA';
    return { icpId: s.icpId, icpName: s.icpName, score: s.score, confidence, matched: s.matched,
      reason: `Termos em comum entre a oferta e o perfil: ${s.matched.join(', ')}.` };
  });
  return { icps: list, segments: [...new Set(top.flatMap((s) => s.segments))].slice(0, 8), transversal, basedOn, none: null };
}

/** Resposta de IM-API-329 `GET /api/v1/company-context/offering-map`. */
export interface OfferingMapItemDto {
  id: string; name: string; kind: string; description: string | null; segments: string[]; icpIds: string[];
  audienceScope: AudienceScope | null; audienceNote: string | null; reviewedAt: string | null; reviewedByName: string | null;
  treatment: OfferingTreatment; suggestion: OfferingSuggestion;
}
export interface OfferingMapDto {
  offerings: OfferingMapItemDto[];
  icps: Array<{ id: string; name: string; segments: string[]; description: string | null }>;
  progress: OfferingMapProgress;
  /** Próxima oferta a revisar (a primeira pendente na ordem do catálogo). */
  nextId: string | null;
}

// ── Pedido ───────────────────────────────────────────────────────────────────────────────────────────────────────────
/** IM-API-330 `POST /api/v1/company-context/offerings/:id/audience`: relacionar a ICPs, ou marcar transversal / sem público. */
export const OfferingAudienceRequest = z.object({
  scope: z.enum(['ICP', 'TRANSVERSAL', 'SEM_PUBLICO']),
  icpIds: z.array(z.string().uuid()).max(50).default([]),
  segments: z.array(z.string().trim().min(1).max(120)).max(30).default([]),
  note: z.string().trim().max(500).nullable().optional(),
  /** A pessoa aceitou a sugestão do InteliMove sem ajuste (fica na auditoria). */
  acceptedSuggestion: z.boolean().default(false),
}).strict().superRefine((v, ctx) => {
  if (v.scope === 'ICP' && !v.icpIds.length) ctx.addIssue({ code: 'custom', path: ['icpIds'], message: 'Escolha ao menos um perfil de cliente ou marque a oferta como transversal ou sem público definido.' });
  if (v.scope !== 'ICP' && v.icpIds.length) ctx.addIssue({ code: 'custom', path: ['icpIds'], message: 'Oferta transversal ou sem público definido não leva perfis relacionados.' });
});
export type OfferingAudienceRequest = z.infer<typeof OfferingAudienceRequest>;
